import { type HistoryLocation, type RouterHistory, createHistory } from "@tanstack/history";
import type { Router } from "../router";

/**
 * A TanStack history that reads and writes through the legacy `Router`
 * instead of `window.history`. The legacy router stays the single owner of the
 * URL in both its modes (browser history and embedded `setRoute`), so TanStack
 * routes and the `Routing` mixin components see the same location while pages
 * move over one at a time.
 */
export function createLegacyRouterHistory(legacy: Router): RouterHistory {
    // Navigations TanStack starts notify its own subscribers; the echoed
    // route-changed must not notify them a second time.
    let applying = false;
    let key = 0;

    const getLocation = (): HistoryLocation => {
        const search = new URLSearchParams(legacy.params).toString();
        const pathname = "/" + legacy.path;
        const searchStr = search ? `?${search}` : "";
        return {
            href: pathname + searchStr + legacy.hash,
            pathname,
            search: searchStr,
            hash: legacy.hash,
            state: { __TSR_index: Math.max(legacy.history.length - 1, 0), __TSR_key: String(key) },
        };
    };

    const apply = (href: string, replace: boolean) => {
        const url = new URL(href, "http://x");
        const params = Object.fromEntries(url.searchParams);
        applying = true;
        try {
            legacy.go(url.pathname.replace(/^\//, "") + url.hash, params, replace);
        } finally {
            applying = false;
        }
        key++;
    };

    const history = createHistory({
        getLocation,
        getLength: () => legacy.history.length,
        pushState: (href) => apply(href, false),
        replaceState: (href) => apply(href, true),
        back: () => void legacy.back(),
        forward: () => legacy.forward(),
        go: (n) => {
            if (n < 0) {
                void legacy.back();
            } else if (n > 0) {
                legacy.forward();
            }
        },
        createHref: (path) => path,
        // A back/forward through the legacy router lands in onRouteChanged.
        notifyOnIndexChange: false,
        destroy: () => legacy.removeEventListener("route-changed", onRouteChanged),
    });

    const onRouteChanged = () => {
        if (applying) {
            return;
        }
        key++;
        history.notify({ type: "PUSH" });
    };
    legacy.addEventListener("route-changed", onRouteChanged);

    return history;
}
